import { getModel } from '../../model/registry'
import { useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors, DragOverlay, pointerWithin } from '@dnd-kit/core'
import { getFromLS, saveToLS } from '../../utils/localStore/localStore'
import useWorkPlanner, { findFirstAvailableSlot } from '../../hooks/useWorkPlanner'
import useToast from '../../hooks/useToast'
import useTranslation from '../../hooks/useTranslation'
import WorkPlannerHeader from './WorkPlannerHeader'
import WorkPlannerQuickNav from './WorkPlannerQuickNav'
import WorkPlannerPickArea from './WorkPlannerPickArea'
import WorkPlannerBoard from './WorkPlannerBoard'
import WorkPlannerBoardVertical, { SLOT_HEIGHT_V } from './WorkPlannerBoardVertical'
import WorkPlannerOperationCard from './WorkPlannerOperationCard'
import { SLOT_WIDTH } from './WorkPlannerSwimlane'

const LS_KEY = 'workPlanner'

const addHours = (date, h) => new Date(date.getTime() + h * 3600000)

const defaultShiftStart = (hour) => {
    const d = new Date()
    d.setHours(hour, 0, 0, 0)
    return d
}

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background: ${({ theme }) => theme.area.bg};
`

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 3rem;
    font-size: var(--font-M);
    color: ${({ theme }) => theme.font.label};
`

const OverlayWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    pointer-events: none;
`

const BlockOverlayCard = styled.div`
    background: ${({ theme }) => `${theme.btn.primary}18`};
    border-radius: 0.4rem;
    border: 1.5px solid ${({ theme }) => theme.btn.primary};
    border-left: 3px solid ${({ theme }) => theme.btn.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.btn.primary}33, 0 2px 8px rgba(0,0,0,0.12);
    padding: 0.3rem 0.45rem;
    cursor: grabbing;
    min-width: 140px;
    max-width: 220px;
`

const BlockOverlayName = styled.span`
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
`

const BlockOverlaySub = styled.span`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    margin-top: 1px;
`

const SnapTimeLabel = styled.div`
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.card.background};
    background: ${({ theme }) => theme.btn.primary};
    border-radius: 0.3rem;
    padding: 2px 7px;
    letter-spacing: 0.03em;
`

const fmtSnapTime = (date) =>
    date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })

const WorkPlannerPage = ({ type }) => {
    const { t } = useTranslation()
    const { showToast } = useToast()
    const config = getModel('plannerViewData')[type]

    if (!config) return <span>No planner config found for: {type}</span>

    const { header: headerConfig, emptyLabel, shiftHours, bufferHours, slotMinutes, snapMinutes, defaultShiftStartHour, opInfoModal: opInfoModalConfig, fields, labels: labelConfig } = config
    const { selector: selectorConfig, plantSelector: plantSelectorConfig } = headerConfig

    // ── Plant selection (persisted) — optional; service planner has no plant selector ──
    const [plantValue, setPlantValue] = useState(() => {
        if (!plantSelectorConfig) return {}
        const saved = getFromLS(LS_KEY, `${type}_plant`)
        return {
            [plantSelectorConfig.id]:      saved?.id      ?? null,
            [plantSelectorConfig.displayField]: saved?.display ?? null
        }
    })

    const handlePlantChange = useCallback((events) => {
        if (!plantSelectorConfig) return
        const newValue = Array.isArray(events)
            ? events.reduce((acc, { target: { name, value } }) => ({ ...acc, [name]: value || null }), {})
            : (events ?? { [plantSelectorConfig.id]: null, [plantSelectorConfig.displayField]: null })
        setPlantValue(newValue)
        setJobValue({ [selectorConfig.id]: null, [selectorConfig.displayField]: null })
        setSelectedOpId(null)
        saveToLS(LS_KEY, `${type}_plant`, {
            id:      newValue[plantSelectorConfig.id]      ?? null,
            display: newValue[plantSelectorConfig.displayField] ?? null
        })
    }, [plantSelectorConfig?.id, plantSelectorConfig?.display, selectorConfig.id, selectorConfig.displayField, type])

    const plantId = plantSelectorConfig ? plantValue[plantSelectorConfig.id] : null

    // ── Job selector config — filtered by plant when one is selected ──────────
    const jobSelectorConfig = useMemo(() => {
        if (!plantId) return selectorConfig
        return {
            ...selectorConfig,
            entityParams: {
                type: 'filter',
                filters: [{ key: 'plantId', value: plantId }]
            }
        }
    }, [selectorConfig, plantId])

    // ── Job selection (persisted) ─────────────────────────────────────────────
    const [jobValue, setJobValue] = useState(() => {
        const saved = getFromLS(LS_KEY, type)
        return {
            [selectorConfig.id]:      saved?.id      ?? null,
            [selectorConfig.displayField]: saved?.display ?? null
        }
    })

    const handleJobChange = useCallback((events) => {
        const newValue = Array.isArray(events)
            ? events.reduce((acc, { target: { name, value } }) => ({ ...acc, [name]: value || null }), {})
            : (events ?? { [selectorConfig.id]: null, [selectorConfig.displayField]: null })
        setJobValue(newValue)
        setSelectedOpId(null)
        saveToLS(LS_KEY, type, {
            id:      newValue[selectorConfig.id]      ?? null,
            display: newValue[selectorConfig.displayField] ?? null
        })
    }, [selectorConfig.id, selectorConfig.displayField, type])

    const jobId = jobValue[selectorConfig.id]

    // ── Operation selection ───────────────────────────────────────────────────
    const [selectedOpId, setSelectedOpId] = useState(null)

    const handleSelectOp = useCallback((opId) => {
        setSelectedOpId(prev => prev === opId ? null : opId)
    }, [])

    // ── Board layout (vertical / horizontal) ─────────────────────────────────
    const [boardLayout, setBoardLayout] = useState(() =>
        (typeof window !== 'undefined' && window.innerWidth <= 768)
            ? 'vertical'
            : (getFromLS(LS_KEY, 'boardLayout') ?? 'horizontal')
    )

    const handleLayoutChange = useCallback((layout) => {
        setBoardLayout(layout)
        saveToLS(LS_KEY, 'boardLayout', layout)
    }, [])

    // On mobile always use vertical regardless of saved preference
    const effectiveBoardLayout = (typeof window !== 'undefined' && window.innerWidth <= 768)
        ? 'vertical'
        : boardLayout

    // ── Shift navigation ──────────────────────────────────────────────────────
    const [shiftStart, setShiftStart] = useState(() => defaultShiftStart(defaultShiftStartHour))

    const handlePrev      = useCallback(() => setShiftStart(d => addHours(d, -shiftHours)), [shiftHours])
    const handleNext      = useCallback(() => setShiftStart(d => addHours(d,  shiftHours)), [shiftHours])
    const handleGoToShift = useCallback((d) => setShiftStart(d), [])

    const handleSelectJob = useCallback((job) => {
        handleJobChange([
            { target: { name: selectorConfig.id,      value: job.id } },
            { target: { name: selectorConfig.displayField,  value: job.display } }
        ])
    }, [handleJobChange, selectorConfig.id, selectorConfig.displayField])

    // ── Data ──────────────────────────────────────────────────────────────────
    const {
        pendingOps, scheduledOps, selectedOp,
        currentOpIds,
        opsLoading,
        allResources, eligibleResourceIds, resourcesByCapability,
        resourcesLoading,
        bookings, bookingsLoading,
        earliestStart,
        resourceEffectiveShifts,
        scheduleOp, unscheduleOp
    } = useWorkPlanner({ jobId, selectedOpId, shiftStart, shiftHours, bufferHours, slotMinutes, plantId, plannerConfig: config })

    // Pick-area click: select + jump to the shift where the op is scheduled
    const handleSelectOpFromPick = useCallback((opId) => {
        const isDeselecting = selectedOpId === opId
        setSelectedOpId(isDeselecting ? null : opId)
        if (!isDeselecting) {
            // Find in scheduledOps — only scheduled ops have a start time to jump to
            const op = scheduledOps.find(o => o.id === opId)
            if (op?.[fields.startTime]) {
                const opStart  = new Date(op[fields.startTime])
                const shiftDay = new Date(opStart)
                shiftDay.setHours(defaultShiftStartHour, 0, 0, 0)
                setShiftStart(shiftDay)
            }
        }
    }, [selectedOpId, scheduledOps, fields.startTime, defaultShiftStartHour])

    // Unique jobs visible on the board (for quick-nav pills)
    const uniqueJobs = useMemo(() => {
        const jobIdField = fields.jobId ?? (config.entities.job + 'Id')
        const seen = new Set()
        const result = []
        Object.values(bookings).forEach(resBookings => {
            resBookings.forEach(b => {
                if (b[jobIdField] && !seen.has(b[jobIdField])) {
                    seen.add(b[jobIdField])
                    result.push({ id: b[jobIdField], display: b[fields.taskSubLabel] })
                }
            })
        })
        return result.sort((a, b) => (a.display ?? '').localeCompare(b.display ?? ''))
    }, [bookings, config, fields])

    // ── DnD ───────────────────────────────────────────────────────────────────
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
    )

    const [activeOp, setActiveOp] = useState(null)
    const [activeDragSource, setActiveDragSource] = useState(null) // 'board' | 'pickArea'
    const [snapPreview, setSnapPreview] = useState(null) // Date | null

    // While a drag is active, use the DRAGGED op's capability to decide which
    // resources accept drops — not the statically selected op.
    const effectiveEligibleResourceIds = useMemo(() => {
        if (activeOp) {
            if (activeOp[fields.capabilityId]) {
                return new Set((resourcesByCapability[activeOp[fields.capabilityId]] ?? []).map(r => r.id))
            }
            // No capability on the dragged op → all resources are eligible
            return new Set(allResources.map(r => r.id))
        }
        return eligibleResourceIds
    }, [activeOp, eligibleResourceIds, resourcesByCapability, allResources, fields])

    const handleDragStart = useCallback(({ active }) => {
        const isBoardDrag = String(active.id).startsWith('board::')
        setActiveDragSource(isBoardDrag ? 'board' : 'pickArea')
        setActiveOp(active.data.current?.op ?? null)
        setSnapPreview(null)
        if (isBoardDrag && active.data.current?.op?.id) {
            setSelectedOpId(active.data.current.op.id)
        }
    }, [])

    const handleDragOver = useCallback(({ active, over }) => {
        if (!over?.data.current?.slotTime) { setSnapPreview(null); return }
        const { slotTime } = over.data.current
        const isBoardDrag = String(active.id).startsWith('board::')
        const snapMs = snapMinutes * 60000
        let snapTime
        if (isBoardDrag && active.rect.translated) {
            const isVertical = effectiveBoardLayout === 'vertical'
            const pixelOffset = isVertical
                ? active.rect.translated.top  - over.rect.top
                : active.rect.translated.left - over.rect.left
            const pixelsPerSlot = isVertical ? SLOT_HEIGHT_V : SLOT_WIDTH
            const rawMs = slotTime.getTime() + (pixelOffset / pixelsPerSlot) * slotMinutes * 60000
            snapTime = new Date(Math.round(rawMs / snapMs) * snapMs)
        } else {
            snapTime = new Date(Math.round(slotTime.getTime() / snapMs) * snapMs)
        }
        setSnapPreview(prev => prev?.getTime() === snapTime.getTime() ? prev : snapTime)
    }, [slotMinutes, snapMinutes, boardLayout])

    const handleDragCancel = useCallback(() => {
        setActiveOp(null)
        setActiveDragSource(null)
        setSnapPreview(null)
    }, [])

    const handleUnschedule = useCallback(async (op) => {
        try {
            await unscheduleOp(op)
        } catch {
            showToast(
                t(labelConfig?.errorSaveTitle   ?? 'manufacturing.planner.error.saveFailed.title'),
                t(labelConfig?.errorSaveMessage ?? 'manufacturing.planner.error.saveFailed.message'),
                'error'
            )
        }
    }, [unscheduleOp, showToast, t, labelConfig])

    const handleDragEnd = useCallback(async ({ active, over }) => {
        setActiveOp(null)
        setActiveDragSource(null)
        setSnapPreview(null)
        if (!over) return

        const op = active.data.current?.op
        const { resource, slotTime } = over.data.current ?? {}
        if (!op || !resource || !slotTime) return

        const resBookings = bookings[resource.id] ?? []
        const wasScheduled = scheduledOps.some(o => o.id === op.id)
        const isBoardDrag = String(active.id).startsWith('board::')

        // Compute sequence constraint for the specific dragged op regardless of selectedOp
        const priorEnds = scheduledOps
            .filter(o => o[fields.sequence] < op[fields.sequence] && o[fields.endTime] && o.id !== op.id)
            .map(o => new Date(o[fields.endTime]).getTime())
        const effectiveEarliestStart = priorEnds.length > 0
            ? new Date(Math.max(...priorEnds))
            : null

        let searchFrom
        if (!wasScheduled) {
            // First-time placement: start from the later of the sequence constraint or the current view start
            const constraintFrom = effectiveEarliestStart ?? shiftStart
            searchFrom = new Date(Math.max(constraintFrom.getTime(), shiftStart.getTime()))
        } else if (isBoardDrag && active.rect.translated) {
            // Board re-drag: snap to where the leading edge of the block lands
            const isVertical = effectiveBoardLayout === 'vertical'
            const pixelOffset = isVertical
                ? active.rect.translated.top  - over.rect.top
                : active.rect.translated.left - over.rect.left
            const pixelsPerSlot = isVertical ? SLOT_HEIGHT_V : SLOT_WIDTH
            const rawMs = slotTime.getTime() + (pixelOffset / pixelsPerSlot) * slotMinutes * 60000
            const snapMs = snapMinutes * 60000
            searchFrom = new Date(Math.round(rawMs / snapMs) * snapMs)
        } else {
            searchFrom = slotTime
        }

        const effectiveShift    = resourceEffectiveShifts[resource.id] ?? null
        const effectiveShiftSlots = effectiveShift?.shiftSlots ?? null
        const boardDate = new Date(shiftStart); boardDate.setHours(0, 0, 0, 0)

        // Reject drops on days the shift does not cover
        const activeDays = effectiveShift?.activeDays ?? []
        const isoDay = boardDate.getDay() === 0 ? 7 : boardDate.getDay()
        const dayIsActive = !activeDays.length || activeDays.includes(isoDay)
        if (!dayIsActive) {
            showToast(
                t(labelConfig?.errorNoSlotTitle   ?? 'manufacturing.planner.error.noSlot.title'),
                t(labelConfig?.errorNoSlotMessage ?? 'manufacturing.planner.error.noSlot.message'),
                'error'
            )
            return
        }

        const validSlot = findFirstAvailableSlot(searchFrom, op[fields.totalTime] ?? 0, resBookings, effectiveEarliestStart, snapMinutes, op.id, effectiveShiftSlots, boardDate, fields.startTime, fields.endTime)

        if (!validSlot) {
            showToast(
                t(labelConfig?.errorNoSlotTitle   ?? 'manufacturing.planner.error.noSlot.title'),
                t(labelConfig?.errorNoSlotMessage ?? 'manufacturing.planner.error.noSlot.message'),
                'error'
            )
            return
        }

        try {
            await scheduleOp(op, resource, validSlot)
            if (!wasScheduled) {
                const remaining = pendingOps.filter(o => o.id !== op.id)
                const next = remaining.find(o => o[fields.sequence] > op[fields.sequence]) ?? remaining[0] ?? null
                setSelectedOpId(next?.id ?? null)
            }
        } catch {
            showToast(
                t(labelConfig?.errorSaveTitle   ?? 'manufacturing.planner.error.saveFailed.title'),
                t(labelConfig?.errorSaveMessage ?? 'manufacturing.planner.error.saveFailed.message'),
                'error'
            )
        }
    }, [bookings, earliestStart, shiftStart, slotMinutes, snapMinutes, boardLayout, scheduleOp, scheduledOps, pendingOps, resourceEffectiveShifts, fields, labelConfig, showToast, t])

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <PageWrapper>
            <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <WorkPlannerHeader
                    plantSelectorConfig={plantSelectorConfig}
                    plantValue={plantValue}
                    onPlantChange={handlePlantChange}
                    selectorConfig={jobSelectorConfig}
                    workOrderValue={jobValue}
                    onWorkOrderChange={handleJobChange}
                    shiftStart={shiftStart}
                    shiftHours={shiftHours}
                    onPrev={handlePrev}
                    onNext={handleNext}
                    boardLayout={effectiveBoardLayout}
                    onLayoutChange={handleLayoutChange}
                />

                <WorkPlannerQuickNav
                    shiftStart={shiftStart}
                    shiftHours={shiftHours}
                    defaultShiftStartHour={defaultShiftStartHour}
                    onGoToShift={handleGoToShift}
                />

                {!jobId && (
                    <EmptyState>{t(emptyLabel)}</EmptyState>
                )}

                {jobId && (
                    <WorkPlannerPickArea
                        pendingOps={pendingOps}
                        scheduledOps={scheduledOps}
                        selectedOpId={selectedOpId}
                        onSelectOp={handleSelectOpFromPick}
                        onUnschedule={handleUnschedule}
                        loading={opsLoading}
                        uniqueJobs={uniqueJobs}
                        jobId={jobId}
                        onSelectJob={handleSelectJob}
                        fieldConfig={fields}
                        labelConfig={labelConfig}
                    />
                )}

                {jobId && effectiveBoardLayout === 'vertical' && (
                    <WorkPlannerBoardVertical
                        selectedOp={selectedOp}
                        allResources={allResources}
                        eligibleResourceIds={effectiveEligibleResourceIds}
                        currentOpIds={currentOpIds}
                        onSelectOp={handleSelectOp}
                        bookings={bookings}
                        earliestStart={earliestStart}
                        shiftStart={shiftStart}
                        shiftHours={shiftHours}
                        bufferHours={bufferHours ?? 0}
                        slotMinutes={slotMinutes}
                        snapPreview={snapPreview}
                        loading={resourcesLoading || bookingsLoading}
                        opInfoModalConfig={opInfoModalConfig}
                        resourceEffectiveShifts={resourceEffectiveShifts}
                        fieldConfig={fields}
                        labelConfig={labelConfig}
                    />
                )}

                {jobId && effectiveBoardLayout === 'horizontal' && (
                    <WorkPlannerBoard
                        selectedOp={selectedOp}
                        allResources={allResources}
                        eligibleResourceIds={effectiveEligibleResourceIds}
                        currentOpIds={currentOpIds}
                        onSelectOp={handleSelectOp}
                        bookings={bookings}
                        earliestStart={earliestStart}
                        shiftStart={shiftStart}
                        shiftHours={shiftHours}
                        bufferHours={bufferHours ?? 0}
                        slotMinutes={slotMinutes}
                        snapPreview={snapPreview}
                        loading={resourcesLoading || bookingsLoading}
                        opInfoModalConfig={opInfoModalConfig}
                        resourceEffectiveShifts={resourceEffectiveShifts}
                        fieldConfig={fields}
                        labelConfig={labelConfig}
                    />
                )}

                <DragOverlay dropAnimation={null}>
                    {activeOp ? (
                        <OverlayWrapper>
                            {activeDragSource === 'board' ? (
                                <BlockOverlayCard>
                                    <BlockOverlayName>
                                        {activeOp[fields.sequence] != null ? `${activeOp[fields.sequence]}. ` : ''}{activeOp[fields.taskLabel]}
                                    </BlockOverlayName>
                                    {activeOp[fields.capabilityName] && (
                                        <BlockOverlaySub>{activeOp[fields.capabilityName]}</BlockOverlaySub>
                                    )}
                                </BlockOverlayCard>
                            ) : (
                                <WorkPlannerOperationCard
                                    op={activeOp}
                                    selected={false}
                                    onSelect={() => {}}
                                    fieldConfig={fields}
                                />
                            )}
                            {snapPreview && (
                                <SnapTimeLabel>{fmtSnapTime(snapPreview)}</SnapTimeLabel>
                            )}
                        </OverlayWrapper>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </PageWrapper>
    )
}

export default WorkPlannerPage
